/*
 * NSE rebuild preview (2026-10-29)
 * - <nse-quote>: opens the "Request a quote" dialog and fills the hidden
 *   variant / URL fields from the currently selected variant before submit.
 *   The dialog is opened NON-modally (dialog.show(), not showModal()) and moved to
 *   <body>: a showModal() dialog sits in the browser top layer and hides Shopify's
 *   hCaptcha challenge, which silently blocks the contact form from submitting.
 * - #request-quote in the URL opens the first quote form on the page (Phase 1 search preview).
 * - <nse-cart-guard>: on the cart page, reloads after a cart change so the
 *   checkout block is re-evaluated once quote-only items are removed.
 */
if (!customElements.get('nse-quote')) {
  customElements.define(
    'nse-quote',
    class NseQuote extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.dialog = this.querySelector('dialog');
        this.backdrop = this.querySelector('.nse-quote__backdrop');
        if (!this.dialog) return;
        this.form = this.dialog.querySelector('form');

        // Move the overlay to <body> so position: fixed is not affected by transformed ancestors.
        // Remove copies left behind by a theme-editor section re-render first.
        [this.dialog.id, this.backdrop?.id].filter(Boolean).forEach((id) => {
          document.querySelectorAll(`body > #${CSS.escape(id)}`).forEach((el) => el.remove());
        });
        if (this.backdrop) document.body.appendChild(this.backdrop);
        document.body.appendChild(this.dialog);

        this.querySelector('.nse-quote__open')?.addEventListener('click', () => this.open());
        this.dialog
          .querySelectorAll('[data-nse-close]')
          .forEach((button) => button.addEventListener('click', () => this.close()));
        this.backdrop?.addEventListener('click', () => this.close());
        this.onKeydown = (event) => {
          if (event.key === 'Escape' && this.dialog.open) this.close();
        };
        document.addEventListener('keydown', this.onKeydown);

        this.form?.addEventListener('submit', (event) => {
          this.fillHiddenFields();
          const button = this.form.querySelector('[type="submit"]');
          if (!button) return;
          // aria-disabled (not disabled) so Shopify's captcha script can still call form.submit()
          button.setAttribute('aria-disabled', 'true');
          button.textContent = 'Sending…';
          clearTimeout(this.resetTimer);
          this.resetTimer = setTimeout(() => {
            button.removeAttribute('aria-disabled');
            button.textContent = 'Send quote request';
          }, 10000);
        });

        // After a post (success or error) Shopify reloads the page; re-open to show the message.
        if (this.dialog.querySelector('[data-nse-open]')) this.open();

        // NSE Phase 1 search preview (2026-10-29): "Request a quote" links on product cards and in
        // "Find my machine" point to <product url>#request-quote; open the first quote form once.
        if (window.location.hash === '#request-quote' && !window.nseQuoteHashOpened && !this.closest('quick-add-modal')) {
          window.nseQuoteHashOpened = true;
          setTimeout(() => this.open(), 0);
        }
      }

      disconnectedCallback() {
        if (this.onKeydown) document.removeEventListener('keydown', this.onKeydown);
      }

      open() {
        this.fillHiddenFields();
        if (this.dialog.open) return;
        this.dialog.show();
        if (this.backdrop) this.backdrop.hidden = false;
        document.body.classList.add('overflow-hidden', 'nse-quote-is-open');
        const focusTarget =
          this.dialog.querySelector('.nse-quote__status') ||
          this.dialog.querySelector('input:not([type="hidden"]), textarea');
        focusTarget?.focus();
      }

      close() {
        this.dialog.close();
        if (this.backdrop) this.backdrop.hidden = true;
        document.body.classList.remove('overflow-hidden', 'nse-quote-is-open');
        this.querySelector('.nse-quote__open')?.focus();
      }

      currentVariant() {
        const scope = this.closest('product-info') || this.closest('.shopify-section') || document;
        const json = scope.querySelector('variant-selects [data-selected-variant]');
        if (!json) return null;
        try {
          return JSON.parse(json.textContent);
        } catch (error) {
          return null;
        }
      }

      fillHiddenFields() {
        const variant = this.currentVariant();
        const variantInput = this.dialog?.querySelector('[data-nse-variant]');
        const urlInput = this.dialog?.querySelector('[data-nse-url]');
        if (variant && variantInput) {
          variantInput.value = variant.title + (variant.sku ? ` (SKU ${variant.sku})` : '');
        }
        if (urlInput && this.dataset.productUrl) {
          const url = new URL(this.dataset.productUrl);
          if (variant && variant.id) url.searchParams.set('variant', variant.id);
          urlInput.value = url.toString();
        }
      }
    }
  );
}

if (!customElements.get('nse-cart-guard')) {
  customElements.define(
    'nse-cart-guard',
    class NseCartGuard extends HTMLElement {
      connectedCallback() {
        if (this.dataset.context !== 'cart' || this.unsubscribe) return;
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => window.location.reload());
        }
      }

      disconnectedCallback() {
        if (this.unsubscribe) this.unsubscribe();
      }
    }
  );
}
